import { useMemo } from "react";

/**
 * Mirrors the form mode logic at the top of ContractReviewFrm.vb (Pass 1 §1.6).
 * Returns a set of boolean flags every form sub-component reads from. Centralising
 * this prevents three different places re-deriving "can I edit responses?".
 *
 * @param {object|null} cr        — the loaded CrDetailDto, or null for new CRs
 * @param {string|number} myEmpId — current login empId
 * @param {boolean} isManager     — whether the current user is a manager (DmsManager flag)
 * @param {boolean} canDelete     — whether the current user may delete THIS CR
 *                                  (server-decided: creator / IT Admin / creator's
 *                                  supervisor / appsettings admin allowlist)
 */
export default function useContractReviewMode(cr, myEmpId, isManager, canDelete) {
    return useMemo(() => {
        const me = Number(myEmpId);

        if (!cr) {
            // Brand-new CR being created.
            return {
                isNew: true,
                isSaved: false,
                isInProgress: false,
                isReady: false,
                isArchived: false,
                isAuthor: true,
                isAuthorSupv: false,
                amResponsible: false,
                isManager,
                canDelete,

                // capability flags
                canEditHeader: true,
                canEditResponses: true,
                canSetResponsibilities: true,
                canDeleteCr: false,           // can't delete a brand-new CR
                canCancel: false,
                canArchive: false,
                canWonLost: false,
                canAttachAfterArchive: false,
                showSetResponsibilitiesTab: true,
                showWonLostBar: false,
            };
        }

        const isAuthor = Number(cr.createdAuthor) === me;
        const isAuthorSupv = false; // resolved server-side in the save path; UI does not enforce additionally
        const isArchived = !!cr.archived;
        const isSaved = !!cr.saved;
        const authorSubmitted = !!cr.authorSubmit;
        const isReady = !!cr.isReady;
        const isInProgress = authorSubmitted && !isSaved && !isArchived && !isReady;

        const amResponsible =
            cr.keyResponses?.some((r) => Number(r.responsible) === me) ||
            cr.salesResponses?.some((r) => Number(r.responsible) === me) ||
            cr.engineeringResponses?.some((r) => Number(r.responsible) === me) ||
            cr.materialsResponses?.some((r) => Number(r.responsible) === me) ||
            cr.qualityResponses?.some((r) => Number(r.responsible) === me);

        const quoteOnly = (cr.quoteNumber ?? 0) > 0 && (cr.salesOrderNum ?? 0) === 0;

        const canEditHeader = isAuthor && (isSaved || !authorSubmitted);
        const canSetResponsibilities = !authorSubmitted && isAuthor;
        // Once author submitted, only the responsible employee (or author) can edit their rows.
        // Server enforces; UI shows the field as editable to anyone with a row.
        const canEditResponses = !isArchived && (isAuthor || amResponsible);
        const canCancel = isAuthor && isInProgress;
        const canArchive = isAuthor && isReady;
        const canWonLost = isAuthor && isReady && (cr.quoteNumber ?? 0) > 0;
        const canDeleteCr = canDelete && !!cr.id;
        const canAttachAfterArchive = isArchived && isManager;

        return {
            isNew: false,
            isSaved,
            isInProgress,
            isReady,
            isArchived,
            isAuthor,
            isAuthorSupv,
            amResponsible: !!amResponsible,
            isManager,
            canDelete,
            quoteOnly,

            canEditHeader,
            canEditResponses,
            canSetResponsibilities,
            canDeleteCr,
            canCancel,
            canArchive,
            canWonLost,
            canAttachAfterArchive,
            showSetResponsibilitiesTab: !authorSubmitted,
            showWonLostBar: isReady && (cr.quoteNumber ?? 0) > 0,
        };
    }, [cr, myEmpId, isManager, canDelete]);
}